import { isAddress } from '@zenlink-interface/format';
import { useMemo } from 'react';
const alwaysTrue = () => true;
/**
 * Create a filter function to apply to a token for whether it matches a particular search query
 * @param search the search query to apply to the token
 */
export function createTokenFilterFunction(search) {
    const validAddress = isAddress(search);
    if (validAddress) {
        const lower = search.toLowerCase();
        return (t) => lower === t.address.toLowerCase();
    }
    const lowerSearchParts = search
        .toLowerCase()
        .split(/\s+/)
        .filter(s => s.length > 0);
    if (lowerSearchParts.length === 0)
        return alwaysTrue;
    const matchesSearch = (s) => {
        const sParts = s
            .toLowerCase()
            .split(/\s+/)
            .filter(s => s.length > 0);
        return lowerSearchParts.every(p => p.length === 0 || sParts.some(sp => sp.startsWith(p) || sp.endsWith(p)));
    };
    return ({ name, symbol }) => Boolean((symbol && matchesSearch(symbol)) || (name && matchesSearch(name)));
}
export function filterTokens(tokens, search) {
    return tokens.filter(createTokenFilterFunction(search));
}
export function balanceComparator(balanceA, balanceB) {
    if (balanceA && balanceB) {
        if (balanceA.asFraction.equalTo(balanceB.asFraction))
            return 0;
        return balanceA.asFraction.greaterThan(balanceB.asFraction) ? -1 : 1;
    }
    else if (balanceA && balanceA.greaterThan('0')) {
        return -1;
    }
    else if (balanceB && balanceB.greaterThan('0')) {
        return 1;
    }
    return 0;
}
export function tokenComparator(balancesMap, pricesMap) {
    return (tokenA, tokenB) => {
        const balanceA = balancesMap === null || balancesMap === void 0 ? void 0 : balancesMap[tokenA.address];
        const balanceB = balancesMap === null || balancesMap === void 0 ? void 0 : balancesMap[tokenB.address];
        const priceA = (pricesMap === null || pricesMap === void 0 ? void 0 : pricesMap[tokenA.address])
            ? balanceA === null || balanceA === void 0 ? void 0 : balanceA.multiply(pricesMap[tokenA.address])
            : undefined;
        const priceB = (pricesMap === null || pricesMap === void 0 ? void 0 : pricesMap[tokenB.address])
            ? balanceB === null || balanceB === void 0 ? void 0 : balanceB.multiply(pricesMap[tokenB.address])
            : undefined;
        const balanceComp = balanceComparator(priceA, priceB);
        if (balanceComp !== 0)
            return balanceComp;
        if (balanceA && balanceB) {
            return balanceA.greaterThan(balanceB) ? -1 : 1;
        }
        else if (balanceA) {
            return -1;
        }
        else if (balanceB) {
            return 1;
        }
        else {
            if (tokenA.symbol && tokenB.symbol) {
                // sort by symbol
                return tokenA.symbol.toLowerCase() < tokenB.symbol.toLowerCase() ? -1 : 1;
            }
            else {
                return tokenA.symbol ? -1 : tokenB.symbol ? -1 : 0;
            }
        }
    };
}
export function useSortedTokensByQuery(tokens, searchQuery) {
    return useMemo(() => {
        if (!tokens)
            return [];
        if (searchQuery === '')
            return tokens;
        const symbolMatch = searchQuery
            .toLowerCase()
            .split(/\s+/)
            .filter(s => s.length > 0);
        if (symbolMatch.length > 1)
            return tokens;
        const exactMatches = [];
        const symbolSubstrings = [];
        const rest = [];
        // sort tokens by exact match -> subtring on symbol match -> rest
        tokens.map((token) => {
            var _a, _b;
            if (((_a = token.symbol) === null || _a === void 0 ? void 0 : _a.toLowerCase()) === symbolMatch[0])
                return exactMatches.push(token);
            else if ((_b = token.symbol) === null || _b === void 0 ? void 0 : _b.toLowerCase().startsWith(searchQuery.toLowerCase().trim()))
                return symbolSubstrings.push(token);
            else
                return rest.push(token);
        });
        return [...exactMatches, ...symbolSubstrings, ...rest];
    }, [tokens, searchQuery]);
}
